import { useCallback, useMemo } from 'react';
import { formatLocalIsoDate } from '../utils/helpers/formatDate';
import { NullableType, Params, StateSchema } from '../utils/helpers/types';
import {
  keyTransformer,
  parseQueryParams,
} from '../utils/searchParams/urlHelpers';
import { useSafeSearchParams } from '../utils/useSafeSearchParams';

/**
 * CMS UI state kept in the query string under the `cms_` prefix (`pushState` by
 * default, so browser back restores it; `replaceState` with `replace`). The
 * counterpart of `useCmsHashState` for state that should survive a copied link —
 * filters, tabs, a comparison selection.
 */
export const useCmsQueryParamsState = <T extends Params>(
  stateSchema: StateSchema<T>,
  options?: {
    skipCmsPrefix?: boolean;
    /**
     * Replace the history entry instead of adding one, for state whose changes are not
     * navigations (a comparison selection).
     */
    replace?: boolean;
  },
): [T, (value: NullableType<T>) => void] => {
  const queryParams = useSafeSearchParams();

  const queryStringState = useMemo(() => {
    const parsed = parseQueryParams<T>(stateSchema, queryParams);
    return parsed;
  }, [queryParams, stateSchema]);

  // Stable while the URL state and options are: consumers hand it to effects and contexts, which
  // would otherwise re-subscribe or rebuild on every render.
  const skipCmsPrefix = options?.skipCmsPrefix;
  const replace = options?.replace;
  const updateState = useCallback(
    (newPartialState: NullableType<T>) => {
      const updated = { ...queryStringState, ...newPartialState };
      updateQueryString(updated, stateSchema, { skipCmsPrefix, replace });
    },
    [queryStringState, stateSchema, skipCmsPrefix, replace],
  );

  return [queryStringState as T, updateState];
};

function updateQueryString<T>(
  newState: Partial<T>,
  stateSchema: StateSchema<T>,
  options?: { skipCmsPrefix?: boolean; replace?: boolean },
): void {
  const params = new URLSearchParams(window.location.search);
  const hash = window.location.hash;

  Object.entries(newState).forEach(([originalKey, value]) => {
    const key = keyTransformer.transform(originalKey, options);
    if ((stateSchema as Record<string, string>)[originalKey] === undefined) {
      return;
    }
    if (value === undefined || value === null) {
      params.delete(key);
    } else if (Array.isArray(value)) {
      if (value.length === 0) {
        params.delete(key);
      } else {
        params.set(key, value.join(','));
      }
    } else if (value instanceof Date) {
      const formatted = formatLocalIsoDate(value);
      params.set(key, formatted);
    } else {
      params.set(key, String(value));
    }
  });

  const query = params.toString();
  const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${hash}`;
  if (options?.replace) window.history.replaceState(null, '', newUrl);
  else window.history.pushState(null, '', newUrl);
}
